import {
    Accordion,
    AccordionItem,
    Box,
    Divider,
    HStack,
    Heading,
    Icon,
    IconButton,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { Droppable } from '@hello-pangea/dnd';
import { LuArrowDown, LuArrowUp, LuX } from 'react-icons/lu';
import { MdDragIndicator } from 'react-icons/md';
import { useSelector } from 'react-redux';

import { accordionHeadingStyling } from '@/components/forms/new/formStyles';
import { classNames } from '@/utils/classNames';
import PlatformAccordion from './PlatformAccordion';

// Drop overlay component for visual feedback when dragging over the group
const DropOverlay = ({ isDraggingOver, darkMode }) => {
    if (!isDraggingOver) return null;

    return (
        <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={darkMode ? 'blue.900' : 'blue.50'}
            opacity={0.7}
            borderRadius="lg"
            zIndex={10}
            pointerEvents="none"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Text
                fontSize="lg"
                fontWeight="bold"
                color={darkMode ? 'blue.200' : 'blue.600'}>
                Drop to add to group
            </Text>
        </Box>
    );
};

const GroupAccordion = ({
    group,
    groupIndex,
    dragHandleProps,
    onCloseGroup,
    onRemovePlatform,
    submitData,
    setSubmitData,
    teamsTrunks,
    callingProfiles,
    OC,
    selectedTrunk,
    setSelectedTrunk,
    previousData,
    trsRegistrations,
    errors,
    touched,
    value,
    isParentDragging = false,
    onMoveUp,
    onMoveDown,
    isFirst = false,
    isLast = false,
    totalItems = 0,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    const platformCount = group.platforms.length;

    const orderNumber = groupIndex + 1;
    const groupAccordionStyles = {
        '.chakra-accordion__item:last-of-type': {
            borderBottomWidth: '2px',
        },
    };

    // Simplified render during parent drag - no nested Droppable to avoid conflicts
    if (isParentDragging) {
        return (
            <Accordion allowToggle defaultIndex={[0]}>
                <AccordionItem
                    key={group.id}
                    border="2px solid"
                    borderColor="green.400"
                    borderRadius="lg"
                    mb={3}
                    bg={darkMode ? 'dark.bgDark' : 'white'}
                    shadow="md"
                    transition="all 0.2s">
                    <HStack spacing={0} position="relative">
                        <Box
                            {...dragHandleProps}
                            mr={2}
                            cursor="grab"
                            _active={{ cursor: 'grabbing' }}
                            display="flex"
                            alignItems="center"
                            px={1}>
                            <Icon
                                as={MdDragIndicator}
                                color={darkMode ? 'white.400' : 'gray.500'}
                                fontSize="20px"
                                _hover={{
                                    color: darkMode ? 'white.300' : 'gray.700',
                                }}
                            />
                        </Box>
                        <Box
                            flex="1"
                            py={3}
                            px={4}
                            display="flex"
                            alignItems="center">
                            <HStack flex="1" spacing={3}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w={8}
                                    h={8}
                                    fontWeight="semibold"
                                    fontSize="sm"
                                    color="messenger.500">
                                    {orderNumber}
                                </Box>
                                <Heading
                                    sx={accordionHeadingStyling}
                                    className={classNames(
                                        darkMode ? 'text-white' : 'text-black',
                                    )}
                                    fontSize="lg">
                                    Branch Routing Group ({platformCount}{' '}
                                    platforms)
                                </Heading>
                            </HStack>
                        </Box>

                        {/* Arrow buttons for reordering */}
                        {totalItems > 1 && (
                            <HStack spacing={0} mr="10px">
                                {!isFirst && onMoveUp && (
                                    <Tooltip label="Increase Priority" hasArrow>
                                        <IconButton
                                            aria-label="Move up (increase priority)"
                                            icon={<LuArrowUp />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="gray"
                                            _hover={{
                                                bg: 'transparent',
                                                color: darkMode
                                                    ? 'white'
                                                    : 'gray.700',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onMoveUp();
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {!isLast && onMoveDown && (
                                    <Tooltip label="Decrease Priority" hasArrow>
                                        <IconButton
                                            aria-label="Move down (decrease priority)"
                                            icon={<LuArrowDown />}
                                            size="sm"
                                            variant="ghost"
                                            color={
                                                darkMode
                                                    ? 'gray.400'
                                                    : 'gray.500'
                                            }
                                            _hover={{
                                                bg: 'transparent',
                                                color: darkMode
                                                    ? 'white'
                                                    : 'gray.700',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onMoveDown();
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </HStack>
                        )}
                        <IconButton
                            aria-label="Remove group"
                            icon={<LuX />}
                            size="sm"
                            variant="ghost"
                            colorScheme="gray"
                            _hover={{
                                bg: 'none',
                            }}
                            mr={2}
                            onClick={(e) => {
                                e.stopPropagation();
                                onCloseGroup(group.id);
                            }}
                        />
                    </HStack>
                    <Box pb={4} pt={2}>
                        <Divider mb={3} />
                        <Accordion allowMultiple defaultIndex={[]}>
                            <Box
                                borderRadius="md"
                                aria-label="routing-group-box">
                                {group.platforms.map((platform, index) => (
                                    <Box key={platform.platformID} mb={2}>
                                        <PlatformAccordion
                                            platformId={platform.platformID}
                                            platform={platform}
                                            errors={errors}
                                            touched={touched}
                                            value={value}
                                            submitData={submitData}
                                            setSubmitData={setSubmitData}
                                            teamsTrunks={teamsTrunks}
                                            callingProfiles={callingProfiles}
                                            OC={OC}
                                            platformIndex={index}
                                            platformNumbersLength={
                                                platformCount
                                            }
                                            selectedTrunk={selectedTrunk}
                                            setSelectedTrunk={setSelectedTrunk}
                                            previousData={previousData}
                                            trsRegistrations={trsRegistrations}
                                            isInGroup={true}
                                            onRemovePlatform={() =>
                                                onRemovePlatform(
                                                    platform.platformID,
                                                )
                                            }
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Accordion>
                    </Box>
                </AccordionItem>
            </Accordion>
        );
    }

    return (
        <Droppable droppableId={`group-${group.id}`} type="PLATFORM">
            {(droppableProvided, droppableSnapshot) => (
                <Box
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    position="relative">
                    {/* Drop overlay for visual feedback */}
                    <DropOverlay
                        isDraggingOver={droppableSnapshot.isDraggingOver}
                        darkMode={darkMode}
                    />

                    <Accordion
                        allowToggle
                        defaultIndex={[0]}
                        sx={groupAccordionStyles}>
                        <AccordionItem
                            key={group.id}
                            border="2px solid var(--chakra-colors-blue-300)"
                            borderRadius="md"
                            mb={3}
                            p={2.5}
                            bg={darkMode ? 'dark.bgDark' : 'white'}
                            shadow={
                                droppableSnapshot.isDraggingOver ? 'lg' : 'md'
                            }
                            transition="all 0.2s">
                            <HStack spacing={0} position="relative">
                                {/* Drag Handle */}
                                <Box
                                    {...dragHandleProps}
                                    mr={3}
                                    cursor="grab"
                                    _active={{ cursor: 'grabbing' }}
                                    display="flex"
                                    alignItems="center">
                                    <Icon
                                        as={MdDragIndicator}
                                        color={
                                            darkMode ? 'white.400' : 'gray.500'
                                        }
                                        fontSize="20px"
                                        _hover={{
                                            color: darkMode
                                                ? 'white.300'
                                                : 'gray.700',
                                        }}
                                    />
                                </Box>

                                {/* Group Header */}
                                <Box
                                    flex="1"
                                    display="flex"
                                    alignItems="center">
                                    <HStack flex="1" spacing={3}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            w={8}
                                            h={8}
                                            fontWeight="semibold"
                                            fontSize="sm"
                                            color="messenger.500">
                                            {orderNumber}
                                        </Box>
                                        <Heading
                                            sx={accordionHeadingStyling}
                                            className={classNames(
                                                darkMode
                                                    ? 'text-white'
                                                    : 'text-black',
                                            )}
                                            fontSize="lg">
                                            Branch Routing Group (
                                            {platformCount} platforms)
                                        </Heading>
                                    </HStack>
                                </Box>

                                {/* Arrow buttons for reordering */}
                                {totalItems > 1 && (
                                    <HStack
                                        spacing={0}
                                        marginEnd="12px!important">
                                        {!isFirst && onMoveUp ? (
                                            <Tooltip
                                                label="Increase Priority"
                                                hasArrow>
                                                <IconButton
                                                    aria-label="Move up (increase priority)"
                                                    icon={<LuArrowUp />}
                                                    size="sm"
                                                    variant="ghost"
                                                    colorScheme="gray"
                                                    _hover={{
                                                        bg: 'transparent',
                                                        color: darkMode
                                                            ? 'white'
                                                            : 'gray.700',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onMoveUp();
                                                    }}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Box
                                                w="32px"
                                                h="32px"
                                                aria-label="placeholder arrow down"
                                                pointerEvents="unset"
                                            />
                                        )}
                                        {!isLast && onMoveDown ? (
                                            <Tooltip
                                                label="Decrease Priority"
                                                hasArrow>
                                                <IconButton
                                                    aria-label="Move down (decrease priority)"
                                                    icon={<LuArrowDown />}
                                                    size="sm"
                                                    variant="ghost"
                                                    colorScheme="gray"
                                                    _hover={{
                                                        bg: 'transparent',
                                                        color: darkMode
                                                            ? 'white'
                                                            : 'gray.700',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onMoveDown();
                                                    }}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Box
                                                w="32px"
                                                h="32px"
                                                aria-label="placeholder arrow up"
                                                pointerEvents="unset"
                                            />
                                        )}
                                    </HStack>
                                )}

                                {/* Close Button */}
                                <IconButton
                                    aria-label="Remove group"
                                    icon={<LuX />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="gray"
                                    _hover={{
                                        bg: 'none',
                                        color: 'red',
                                    }}
                                    me="6px"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCloseGroup(group.id);
                                    }}
                                />
                            </HStack>

                            <Box py={2}>
                                <Divider mb={3} />

                                <Accordion allowMultiple defaultIndex={[]}>
                                    <Box borderRadius="md">
                                        {group.platforms.map(
                                            (platform, index) => (
                                                <Box
                                                    key={platform.platformID}
                                                    mb={2}>
                                                    <PlatformAccordion
                                                        platformId={
                                                            platform.platformID
                                                        }
                                                        platform={platform}
                                                        errors={errors}
                                                        touched={touched}
                                                        value={value}
                                                        submitData={submitData}
                                                        setSubmitData={
                                                            setSubmitData
                                                        }
                                                        teamsTrunks={
                                                            teamsTrunks
                                                        }
                                                        callingProfiles={
                                                            callingProfiles
                                                        }
                                                        OC={OC}
                                                        platformIndex={index}
                                                        platformNumbersLength={
                                                            platformCount
                                                        }
                                                        selectedTrunk={
                                                            selectedTrunk
                                                        }
                                                        setSelectedTrunk={
                                                            setSelectedTrunk
                                                        }
                                                        previousData={
                                                            previousData
                                                        }
                                                        trsRegistrations={
                                                            trsRegistrations
                                                        }
                                                        isInGroup={true}
                                                        onRemovePlatform={() =>
                                                            onRemovePlatform(
                                                                platform.platformID,
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            ),
                                        )}
                                        {droppableProvided.placeholder}

                                        {/* Empty state */}
                                        {group.platforms.length === 0 && (
                                            <Box
                                                p={4}
                                                textAlign="center"
                                                color="gray.500"
                                                fontSize="sm">
                                                Drop platforms here to add them
                                                to this group
                                            </Box>
                                        )}
                                    </Box>
                                </Accordion>
                            </Box>
                        </AccordionItem>
                    </Accordion>
                </Box>
            )}
        </Droppable>
    );
};

export default GroupAccordion;
